// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Rectangle overlap check
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

const generateEdgePoints = (points, segments = 4) => {
    const edgePoints = [];

    for (let i = 0; i < points.length; i++) {
        const start = points[i];
        const end = points[(i + 1) % points.length];

        edgePoints.push(start);

        for (let j = 1; j < segments; j++) {
            const t = j / segments;
            edgePoints.push({
                x: start.x + t * (end.x - start.x),
                y: start.y + t * (end.y - start.y),
            });
        }
    }

    return edgePoints;
};

const doesRectangleOverlap = (rect1, rect2) => {
    const rect1SampledPoints = generateEdgePoints(rect1.points, 4);
    const rect2SampledPoints = generateEdgePoints(rect2.points, 4);

    return rect1SampledPoints.some(point => isPointInsideRectangle(point, rect2)) ||
           rect2SampledPoints.some(point => isPointInsideRectangle(point, rect1));
};


const getPolygonIntersection = (polygon1, polygon2) => {
    let intersection = [];
    let seenPoints = new Set();

    polygon1.forEach((point, i) => {
        const nextPoint = polygon1[(i + 1) % polygon1.length];

        polygon2.forEach((p, j) => {
            const nextP = polygon2[(j + 1) % polygon2.length];

            const intersectionPoint = getLineIntersection(point, nextPoint, p, nextP);
            if (intersectionPoint) {
                const key = `${intersectionPoint.x},${intersectionPoint.y}`;
                if (!seenPoints.has(key)) {
                    intersection.push(intersectionPoint);
                    seenPoints.add(key);
                }
            }
        });
    });

    polygon1.forEach((point) => {
        if (isPointInsidePolygon(point, polygon2)) {
            const key = `${point.x},${point.y}`;
            if (!seenPoints.has(key)) {
                intersection.push(point);
                seenPoints.add(key);
            }
        }
    });

    polygon2.forEach((point) => {
        if (isPointInsidePolygon(point, polygon1)) {
            const key = `${point.x},${point.y}`;
            if (!seenPoints.has(key)) {
                intersection.push(point);
                seenPoints.add(key);
            }
        }
    });

    return intersection;
};



const getLineIntersection = (p1, p2, p3, p4) => {
    const s1_x = p2.x - p1.x;
    const s1_y = p2.y - p1.y;
    const s2_x = p4.x - p3.x;
    const s2_y = p4.y - p3.y;

    const s = (-s1_y * (p1.x - p3.x) + s1_x * (p1.y - p3.y)) / (-s2_x * s1_y + s1_x * s2_y);
    const t = (s2_x * (p1.y - p3.y) - s2_y * (p1.x - p3.x)) / (-s2_x * s1_y + s1_x * s2_y);

    if (s >= 0 && s <= 1 && t >= 0 && t <= 1) {
        return {
            x: p1.x + (t * s1_x),
            y: p1.y + (t * s1_y)
        };
    }
    return null;
};

const isPointInsidePolygon = (point, polygon) => {
    let inside = false;
    let j = polygon.length - 1;

    for (let i = 0; i < polygon.length; i++) {
        const xi = polygon[i].x, yi = polygon[i].y;
        const xj = polygon[j].x, yj = polygon[j].y;

        const intersect = ((yi > point.y) !== (yj > point.y)) &&
            (point.x < ((xj - xi) * (point.y - yi) / (yj - yi) + xi));
        if (intersect) inside = !inside;
        j = i;
    }
    return inside;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Circle overlap check
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

const doesCircleOverlap = (circ1, circ2) => {
    const dx = circ1.x - circ2.x;
    const dy = circ1.y - circ2.y;
    const distanceSquared = dx * dx + dy * dy;
    const radiusSum = circ1.radius + circ2.radius;
    return distanceSquared <= radiusSum * radiusSum;
}

const getCircleOverlapPolygon = (circle1, circle2) => {
    const circle1Points = generateEllipsePoints(circle1);
    const circle2Points = generateEllipsePoints(circle2);

    const insideCircle1 = circle2Points.filter(p =>
        isPointInsideEllipse(p, circle1)
    );

    const insideCircle2 = circle1Points.filter(p =>
        isPointInsideEllipse(p, circle2)
    );

    const edgeIntersections = [];
    for (let i = 0; i < circle1Points.length; i++) {
        const p1 = circle1Points[i];
        const p2 = circle1Points[(i + 1) % circle1Points.length];

        for (let j = 0; j < circle2Points.length; j++) {
            const q1 = circle2Points[j];
            const q2 = circle2Points[(j + 1) % circle2Points.length];

            const intersection = getLineIntersection(p1, p2, q1, q2);
            if (intersection) edgeIntersections.push(intersection);
        }
    }

    return [...edgeIntersections, ...insideCircle1, ...insideCircle2];
};

const isPointInsideEllipse = (point, ellipse) => {
    const { x: cx, y: cy, radius, scaleX, scaleY, rotation } = ellipse;

    const translated = { x: point.x - cx, y: point.y - cy };

    const angle = (rotation * Math.PI) / 180;

    const rotated = {
        x: translated.x * Math.cos(-angle) - translated.y * Math.sin(-angle),
        y: translated.x * Math.sin(-angle) + translated.y * Math.cos(-angle)
    };

    const rx = radius * scaleX;
    const ry = radius * scaleY;

    return (rotated.x * rotated.x) / (rx * rx) + (rotated.y * rotated.y) / (ry * ry) <= 1;
};


// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Rectangle and Circle overlap check
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~


const doesRecCircOverlap = (mask1, mask2) => {
    const rect = mask1.type === "rectangle" ? mask1 : mask2;
    const circle = mask1.type === "circle" ? mask1 : mask2;

    const circlePoints = generateEllipsePoints(circle);

    const circleHitsRect = circlePoints.some(point => 
        isPointInsideRectangle(point, rect)
    );
    if (circleHitsRect) return true;

    const rectCorners = getRectangleCorners(rect);
    const rectHitsCircle = rectCorners.some(corner =>
        isPointInsideCircle(corner, circle)
    );
    if (rectHitsCircle) return true;

    const rectFullyInsideCircle = rectCorners.every(corner =>
        isPointInsideCircle(corner, circle)
    );
    if (rectFullyInsideCircle) return true;

    const circleCenterInsideRect = isPointInsideRectangle({ x: circle.x, y: circle.y }, rect);
    if (circleCenterInsideRect && circle.radius * circle.scaleX < rect.width / 2 && circle.radius * circle.scaleY < rect.height / 2) {
        return true;
    }

    return false;
};

function getRectangleCorners(rect) {
    const { x, y, scaleX, scaleY } = rect;
    const width = 100 * (scaleX || 1);
    const height = 100 * (scaleY || 1);
    return [
        { x: x, y: y },
        { x: x + width, y: y },
        { x: x, y: y + height },
        { x: x + width, y: y + height },
    ];
}

function isPointInsideCircle(point, circle) {
    const dx = point.x - circle.x;
    const dy = point.y - circle.y;
    const radiusX = circle.radius * (circle.scaleX || 1);
    const radiusY = circle.radius * (circle.scaleY || 1);
    return (dx * dx) / (radiusX * radiusX) + (dy * dy) / (radiusY * radiusY) <= 1;
}


const getRecCircOverlapPolygon = (mask1, mask2) => {
    const rect = mask1.type === "rectangle" ? mask1 : mask2;
    const circle = mask1.type === "circle" ? mask1 : mask2;
    const circlePoints = generateEllipsePoints(circle);
    const rectPoints = rect.points;

    const intersectionPoints = [];
    for (let i = 0; i < rectPoints.length; i++) {
        const p1 = rectPoints[i];
        const p2 = rectPoints[(i + 1) % rectPoints.length];

        const edgeIntersections = getEllipseLineIntersections(circle, p1, p2);
        intersectionPoints.push(...edgeIntersections);
    }

    const insideRectanglePoints = rectPoints.filter(p => {
        const { x: cx, y: cy, radius, scaleX, scaleY, rotation } = circle;

        const translatedP = { x: p.x - cx, y: p.y - cy };

        const angle = (rotation * Math.PI) / 180;

        const rotatedP = {
            x: translatedP.x * Math.cos(-angle) - translatedP.y * Math.sin(-angle),
            y: translatedP.x * Math.sin(-angle) + translatedP.y * Math.cos(-angle)
        };

        const scaledRadiusX = radius * scaleX;
        const scaledRadiusY = radius * scaleY;

        const insideEllipse = (Math.pow(rotatedP.x, 2) / Math.pow(scaledRadiusX, 2)) + (Math.pow(rotatedP.y, 2) / Math.pow(scaledRadiusY, 2)) <= 1;

        return insideEllipse;
    });

    const insideCirclePoints = circlePoints.filter(p => isPointInsideRectangle(p, rect));

    // console.log("intersectionPoints", intersectionPoints, "\n insideRectanglePoints", insideRectanglePoints, "\n insideCirclePoints", insideCirclePoints)
    return [...intersectionPoints, ...insideRectanglePoints, ...insideCirclePoints];
};

const getEllipseLineIntersections = (ellipse, p1, p2) => {
    const { x: cx, y: cy, radius, scaleX, scaleY, rotation } = ellipse;
    const rx = radius * scaleX;
    const ry = radius * scaleY;

    const angle = (rotation * Math.PI) / 180;

    const translate = (p) => ({
        x: p.x - cx,
        y: p.y - cy
    });

    const rotate = (p, angle) => ({
        x: p.x * Math.cos(angle) - p.y * Math.sin(angle),
        y: p.x * Math.sin(angle) + p.y * Math.cos(angle)
    });

    const inverseRotate = (p, angle) => ({
        x: p.x * Math.cos(-angle) - p.y * Math.sin(-angle),
        y: p.x * Math.sin(-angle) + p.y * Math.cos(-angle)
    });

    const translatedP1 = translate(p1);
    const translatedP2 = translate(p2);

    const rotatedP1 = inverseRotate(translatedP1, angle);
    const rotatedP2 = inverseRotate(translatedP2, angle);

    const x1 = rotatedP1.x, y1 = rotatedP1.y;
    const x2 = rotatedP2.x, y2 = rotatedP2.y;

    const dx = x2 - x1;
    const dy = y2 - y1;

    const A = (dx * dx) / (rx * rx) + (dy * dy) / (ry * ry);
    const B = (2 * x1 * dx) / (rx * rx) + (2 * y1 * dy) / (ry * ry);
    const C = (x1 * x1) / (rx * rx) + (y1 * y1) / (ry * ry) - 1;

    const discriminant = B * B - 4 * A * C;

    if (discriminant < 0) return [];

    const sqrtD = Math.sqrt(discriminant);
    const t1 = (-B + sqrtD) / (2 * A);
    const t2 = (-B - sqrtD) / (2 * A);

    const intersections = [];

    [t1, t2].forEach(t => {
        if (t >= 0 && t <= 1) {
            const intersectRotated = {
                x: x1 + t * dx,
                y: y1 + t * dy
            };

            const finalPoint = rotate(intersectRotated, angle);

            intersections.push({
                x: finalPoint.x + cx,
                y: finalPoint.y + cy
            });
        }
    });

    return intersections;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Polygon overlap check
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

const doesPolygonOverlap = (polygon1, polygon2) => {
    for (let i = 0; i < polygon1.length; i++) {
        const p1 = polygon1[i];
        const p2 = polygon1[(i + 1) % polygon1.length]; 
        for (let j = 0; j < polygon2.length; j++) {
            const q1 = polygon2[j];
            const q2 = polygon2[(j + 1) % polygon2.length];

            const intersection = getLineIntersection(p1, p2, q1, q2);
            if (intersection) {
                return true;
            }
        }
    }
    
    for (let i = 0; i < polygon1.length; i++) {
        if (isPointInsidePolygon(polygon1[i], polygon2)) {
            return true;
        }
    }

    for (let i = 0; i < polygon2.length; i++) {
        if (isPointInsidePolygon(polygon2[i], polygon1)) {
            return true;
        }
    }

    return false;
};

const doesRectPolygonOverlap = (rect, polygon) => {
    let overlap = false;
    let intersectionPoints = [];

    for (let i = 0; i < rect.points.length; i++) {
        const rectPoint1 = rect.points[i];
        const rectPoint2 = rect.points[(i + 1) % rect.points.length];
        
        for (let j = 0; j < polygon.length; j++) {
            const polyPoint1 = polygon[j];
            const polyPoint2 = polygon[(j + 1) % polygon.length];

            const intersection = getLineIntersection(rectPoint1, rectPoint2, polyPoint1, polyPoint2);
            if (intersection) {
                intersectionPoints.push(intersection);
                overlap = true;
            }
        }
    }

    polygon.forEach((point) => {
        if (isPointInsideRectangle(point, rect)) {
            intersectionPoints.push(point);
            overlap = true;
        }
    });

    rect.points.forEach((point) => {
        if (isPointInsidePolygon(point, polygon)) {
            intersectionPoints.push(point);
            overlap = true;
        }
    });

    return { overlap, intersectionPoints };
};

const doesCirclePolygonOverlap = (circle, polygon) => {
    let overlap = false;
    let intersectionPoints = [];

    // Check if any edge of the polygon intersects with the circle
    for (let i = 0; i < polygon.length; i++) {
        const polyPoint1 = polygon[i];
        const polyPoint2 = polygon[(i + 1) % polygon.length];

        const edgeIntersections = getEllipseLineIntersections(circle, polyPoint1, polyPoint2);
        if (edgeIntersections.length > 0) {
            intersectionPoints.push(...edgeIntersections);
            overlap = true;
        }
    }

    // Check if any point of the polygon is inside the circle
    polygon.forEach((point) => {
        if (isPointInsideEllipse(point, circle)) {
            intersectionPoints.push(point);
            overlap = true;
        }
    });

    // Check if any point of the circle is inside the polygon
    const circlePoints = generateEllipsePoints(circle);
    circlePoints.forEach((point) => {
        if (isPointInsidePolygon(point, polygon)) {
            intersectionPoints.push(point);
            overlap = true;
        }
    });

    return { overlap, intersectionPoints };
};



// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Helper functions
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

const generateEllipsePoints = (mask, numPoints = 100) => {
    const { x, y, radius, scaleX, scaleY, rotation } = mask;
    const angle = (rotation * Math.PI) / 180;

    const fixedPoints = [
        { x: x, y: y - (radius * scaleY) },
        { x: x + (radius * scaleX), y: y },
        { x: x, y: y + (radius * scaleY) },
        { x: x - (radius * scaleX), y: y }
    ];

    let points = [];
    let closestIndices = new Array(4).fill(0);
    let minDists = new Array(4).fill(Infinity);

    for (let i = 0; i < numPoints; i++) {
        const theta = (i / numPoints) * 2 * Math.PI;
        let px = x + Math.cos(theta) * (radius * scaleX);
        let py = y + Math.sin(theta) * (radius * scaleY);

        const rotatedX = (px - x) * Math.cos(angle) - (py - y) * Math.sin(angle) + x;
        const rotatedY = (px - x) * Math.sin(angle) + (py - y) * Math.cos(angle) + y;

        points.push({ x: rotatedX, y: rotatedY });

        for (let j = 0; j < 4; j++) {
            const dist = Math.hypot(rotatedX - fixedPoints[j].x, rotatedY - fixedPoints[j].y);
            if (dist < minDists[j]) {
                minDists[j] = dist;
                closestIndices[j] = i;
            }
        }
    }

    let orderedPoints = [];
    let startIndex = closestIndices[0];

    for (let i = 0; i < numPoints; i++) {
        let index = (startIndex + i) % numPoints;
        orderedPoints.push(points[index]);
    }

    return orderedPoints;
};

const isPointInsideRectangle = (point, rect) => {
    const [A, B, C, D] = rect.points;

    const cross = (P1, P2, P3) => {
        return (P2.x - P1.x) * (P3.y - P1.y) - (P2.y - P1.y) * (P3.x - P1.x);
    };

    const inside =
        cross(A, B, point) >= 0 &&
        cross(B, C, point) >= 0 &&
        cross(C, D, point) >= 0 &&
        cross(D, A, point) >= 0;

    return inside;
};


// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Main function to check for overlap
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~


const checkForOverlap = (maskList) => {
    const overlapData = [];
    if (!maskList || maskList.length < 2) return overlapData;

    for (let i = 0; i < maskList.length; i++) {
        const mask1 = maskList[i];
        for (let j = i + 1; j < maskList.length; j++) {
            const mask2 = maskList[j];

            if (mask1.id === mask2.id) continue;            
            if (mask1.maskIn === false && mask2.maskIn === false) continue;
            if (mask1.maskIn === true && mask2.maskIn === true) continue;
            if (mask1.layer > mask2.layer) continue;
            if (mask1.maskIn === true) continue;

            let overlap = false;
            let overlapCheck = false;
            let overlapPoints = [];

            if (mask1.type === "rectangle" && mask2.type === "rectangle") {
                overlap = doesRectangleOverlap(mask1, mask2);
                if (overlap) overlapCheck = doesRectangleOverlap(mask2, mask1);
                if (overlapCheck) overlapPoints = getPolygonIntersection(mask1.points, mask2.points);
            } else if (mask1.type === "circle" && mask2.type === "circle") {
                overlap = doesCircleOverlap(mask1, mask2);
                if (overlap) overlapCheck = doesCircleOverlap(mask2, mask1);
                if (overlapCheck) overlapPoints = getCircleOverlapPolygon(mask1, mask2);
            } else if (
                (mask1.type === "rectangle" && mask2.type === "circle") ||
                (mask1.type === "circle" && mask2.type === "rectangle")
            ) {
                overlap = doesRecCircOverlap(mask1, mask2);
                if (overlap) overlapCheck = doesRecCircOverlap(mask2, mask1);
                if (overlapCheck) overlapPoints = getRecCircOverlapPolygon(mask1, mask2);
            }
            // Check for polygon overlap
            else if (
                (mask1.type === "polygon" && mask2.type === "rectangle") ||
                (mask1.type === "rectangle" && mask2.type === "polygon")
            ) {
                const rect = mask1.type === "rectangle" ? mask1 : mask2;
                const poly = mask1.type === "polygon" ? mask1 : mask2;                
                const { overlap: polyRectOverlap, intersectionPoints } = doesRectPolygonOverlap(rect, poly.points);
                overlap = polyRectOverlap;
                overlapPoints = intersectionPoints;
            }
            else if (
                (mask1.type === "polygon" && mask2.type === "circle") ||
                (mask1.type === "circle" && mask2.type === "polygon")
            ) {
                const circ = mask1.type === "circle" ? mask1 : mask2;
                const poly = mask1.type === "polygon" ? mask1 : mask2;
                const { overlap: polyCircOverlap, intersectionPoints } = doesCirclePolygonOverlap(circ, poly.points);
                overlap = polyCircOverlap;
                overlapPoints = intersectionPoints;
            } else if (
                (mask1.type === "polygon" && mask2.type === "polygon")
            ) {
                overlap = overlap = doesPolygonOverlap(mask1.points, mask2.points);
                if (overlap) overlapCheck = doesPolygonOverlap(mask2.points, mask1.points);
                if (overlapCheck) {
                    overlapPoints = getPolygonIntersection(mask1.points, mask2.points);
                    overlapPoints = [...overlapPoints, overlapPoints[0]];
                }

            }

            overlapData.push({
                mask1Id: mask1.id,
                mask2Id: mask2.id,
                mask1Type: mask1.type,
                mask2Type: mask2.type,
                mask1Name: mask1.name,
                mask2Name: mask2.name,
                mask1Layer: mask1.layer,
                mask2Layer: mask2.layer,
                mask1MaskIn: mask1.maskIn,
                mask2MaskIn: mask2.maskIn,
                overlap: overlap ? true : false,
                overlapPoints: overlapPoints
            });

        }
    }
    return (overlapData)
};


export default checkForOverlap;